import { type SurveyProductionRateDto, SurveyProductionRatePrimaryConstraint } from '@@types/api.types';
import { z } from 'zod/v4';

export const MRL_PRODUCTION_RATE_QUALIFIER_MAX_LENGTH = 80;

const requiredStringSchema = z.string().trim().min(1, 'Required');

const nonNegativeNumberSchema = z.preprocess(
  (value) => (value === null || value === '' ? undefined : value),
  z
    .number({ error: (issue) => (issue.input === undefined ? 'Required' : 'Must be a number') })
    .min(0, 'Must be zero or greater'),
);

const productionRateAssumptionSchema = z
  .object({
    inPlace: z.boolean(),
    qualifier: z
      .string()
      .trim()
      .max(MRL_PRODUCTION_RATE_QUALIFIER_MAX_LENGTH, 'Must be 80 characters or less')
      .optional(),
  })
  .check((ctx) => {
    if (ctx.value.inPlace || ctx.value.qualifier) {
      return;
    }

    ctx.issues.push({
      code: 'custom',
      input: ctx.value,
      message: 'Required when assumption is not in place',
      path: ['qualifier'],
    });
  });

export const mrlProductionRateLowRateSchema = z
  .object({
    lowRateProduction: z.object({
      durationMonths: nonNegativeNumberSchema,
      evidence: requiredStringSchema,
      rateUnitsPerMonth: nonNegativeNumberSchema,
    }),
  })
  .strip();

export const mrlProductionRateFullRateSchema = z
  .object({
    fullRateProduction: z.object({
      evidence: requiredStringSchema,
      leadTimeWeeks: nonNegativeNumberSchema,
      rateUnitsPerMonth: nonNegativeNumberSchema,
    }),
  })
  .strip();

export const mrlProductionRateSurgeSchema = z
  .object({
    surgeProduction: z.object({
      evidence: requiredStringSchema,
      peakRateUnitsPerMonth: nonNegativeNumberSchema,
      primaryConstraint: z.enum(SurveyProductionRatePrimaryConstraint, {
        message: 'Select a primary constraint',
      }),
      rampTimeWeeks: nonNegativeNumberSchema,
    }),
  })
  .strip();

export const mrlProductionRateDetailsSchema = z
  .object({
    fullRateProduction: mrlProductionRateFullRateSchema.shape.fullRateProduction,
    lowRateProduction: mrlProductionRateLowRateSchema.shape.lowRateProduction,
    surgeProduction: mrlProductionRateSurgeSchema.shape.surgeProduction,
  })
  .strip();

export const mrlProductionRateAssumptionsSchema = z
  .object({
    assumptions: z.object({
      criticalMaterials: productionRateAssumptionSchema,
      facilityTooling: productionRateAssumptionSchema,
      funding: productionRateAssumptionSchema,
      regulatory: productionRateAssumptionSchema,
      supplyChain: productionRateAssumptionSchema,
      workforce: productionRateAssumptionSchema,
    }),
  })
  .strip();

export const mrlProductionRateSchema = z
  .object({
    assumptions: mrlProductionRateAssumptionsSchema.shape.assumptions,
    fullRateProduction: mrlProductionRateFullRateSchema.shape.fullRateProduction,
    lowRateProduction: mrlProductionRateLowRateSchema.shape.lowRateProduction,
    surgeProduction: mrlProductionRateSurgeSchema.shape.surgeProduction,
  })
  .strip() satisfies z.ZodType<SurveyProductionRateDto>;
